import { useState } from 'react';
import {
  useFolders,
  useCreateFolder,
  useUpdateFolder,
  useDeleteFolder,
  useTags,
  useCreateTag,
  useUpdateTag,
  useDeleteTag,
  useMe,
} from '../lib/queries';
import { formatDate, folderDotColor } from '../components/ui';
import { ShareDialog } from '../components/ShareDialog';
import type { Folder, Tag } from '@scribe/core/types';

/** Orders top-level folders first, each immediately followed by its direct children
 * (single level of visual nesting — Folder itself only models one parent_id hop). */
function sortedWithChildren(items: Folder[]): { folder: Folder; isChild: boolean }[] {
  const byParent = new Map<string, Folder[]>();
  const roots: Folder[] = [];
  for (const f of items) {
    if (f.parent_id) {
      const list = byParent.get(f.parent_id) ?? [];
      list.push(f);
      byParent.set(f.parent_id, list);
    } else {
      roots.push(f);
    }
  }
  const out: { folder: Folder; isChild: boolean }[] = [];
  for (const root of roots) {
    out.push({ folder: root, isChild: false });
    for (const child of byParent.get(root.id) ?? []) {
      out.push({ folder: child, isChild: true });
    }
  }
  // Folders whose parent_id points at something not in this page still need to render.
  const seen = new Set(out.map((o) => o.folder.id));
  for (const f of items) {
    if (!seen.has(f.id)) out.push({ folder: f, isChild: false });
  }
  return out;
}

export function FoldersScreen() {
  const { data: folders, isLoading, error, refetch } = useFolders();
  // SHARE-4/#20: indicator only (no scope param for folders, decision 2 in
  // docs/specs/share-4.md) — `undefined` while `useMe` hasn't resolved yet suppresses the
  // chip rather than showing it on every row.
  const { data: me } = useMe();
  const createFolder = useCreateFolder();
  const updateFolder = useUpdateFolder();
  const deleteFolder = useDeleteFolder();
  const [name, setName] = useState('');
  const [shareFolderId, setShareFolderId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    createFolder.mutate({ name: name.trim() }, { onSuccess: () => setName('') });
  }

  function startRename(f: Folder) {
    setRenamingId(f.id);
    setRenameDraft(f.name);
  }

  function saveRename(id: string) {
    const trimmed = renameDraft.trim();
    if (!trimmed) return;
    updateFolder.mutate({ id, name: trimmed }, { onSuccess: () => setRenamingId(null) });
  }

  const rows = folders ? sortedWithChildren(folders.items) : [];

  return (
    <div className="card">
      <div className="card-head">
        <h1>Folders</h1>
      </div>
      <p className="muted">
        Organize recordings into folders and share a whole folder with a user or team.
      </p>

      <form className="row" onSubmit={submit}>
        <input
          placeholder="New folder name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button disabled={!name.trim() || createFolder.isPending}>
          {createFolder.isPending ? 'Creating…' : 'Create folder'}
        </button>
      </form>
      {createFolder.isError && <p className="error">{(createFolder.error as Error).message}</p>}

      {error && (
        <div className="banner banner-danger">
          <p>{(error as Error).message || 'Failed to load folders.'}</p>
          <button className="sm" onClick={() => void refetch()}>
            Retry
          </button>
        </div>
      )}
      {isLoading && <p className="muted">Loading folders…</p>}
      {folders && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Created</th>
                <th className="right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td className="empty" colSpan={3}>
                    No folders yet — create one above to start organizing recordings.
                  </td>
                </tr>
              )}
              {rows.map(({ folder: f, isChild }) => (
                <tr key={f.id}>
                  <td>
                    <span
                      className={'folder-row-main' + (isChild ? ' is-child' : '')}
                      style={isChild ? { marginLeft: 22 } : undefined}
                    >
                      <span
                        className="folder-dot"
                        style={{ background: folderDotColor(f.id) }}
                        aria-hidden="true"
                      />
                      {renamingId === f.id ? (
                        <input
                          aria-label={`Rename ${f.name}`}
                          value={renameDraft}
                          autoFocus
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveRename(f.id);
                            if (e.key === 'Escape') setRenamingId(null);
                          }}
                        />
                      ) : (
                        <span className="folder-name">{f.name}</span>
                      )}
                      {me != null && f.owner_id !== me.id && (
                        <span className="tag">Shared with you</span>
                      )}
                    </span>
                  </td>
                  <td>{formatDate(f.created_at)}</td>
                  <td className="right">
                    <div className="actions">
                      {renamingId === f.id ? (
                        <>
                          <button
                            className="sm"
                            disabled={!renameDraft.trim() || updateFolder.isPending}
                            onClick={() => saveRename(f.id)}
                          >
                            {updateFolder.isPending ? 'Saving…' : 'Save'}
                          </button>
                          <button className="ghost sm" onClick={() => setRenamingId(null)}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="ghost sm" onClick={() => startRename(f)}>
                            Rename
                          </button>
                          <button className="ghost sm" onClick={() => setShareFolderId(f.id)}>
                            Share
                          </button>
                          {confirmDelete === f.id ? (
                            <>
                              <button
                                className="danger sm"
                                disabled={deleteFolder.isPending}
                                onClick={() =>
                                  deleteFolder.mutate(f.id, {
                                    onSettled: () => setConfirmDelete(null),
                                  })
                                }
                              >
                                Confirm
                              </button>
                              <button className="ghost sm" onClick={() => setConfirmDelete(null)}>
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button className="ghost sm" onClick={() => setConfirmDelete(f.id)}>
                              Delete
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {updateFolder.isError && <p className="error">{(updateFolder.error as Error).message}</p>}
      {deleteFolder.isError && <p className="error">{(deleteFolder.error as Error).message}</p>}

      {shareFolderId && (
        <ShareDialog
          resourceType="folder"
          resourceId={shareFolderId}
          onClose={() => setShareFolderId(null)}
        />
      )}
    </div>
  );
}

export function TagsScreen() {
  const { data: tags, isLoading, error, refetch } = useTags();
  const createTag = useCreateTag();
  const updateTag = useUpdateTag();
  const deleteTag = useDeleteTag();
  const [name, setName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    createTag.mutate({ name: name.trim() }, { onSuccess: () => setName('') });
  }

  function startRename(t: Tag) {
    setRenamingId(t.id);
    setRenameDraft(t.name);
  }

  function saveRename(id: string) {
    const trimmed = renameDraft.trim();
    if (!trimmed) return;
    updateTag.mutate({ id, name: trimmed }, { onSuccess: () => setRenamingId(null) });
  }

  const items = tags?.items ?? [];

  return (
    <div className="card">
      <div className="card-head">
        <h1>Tags</h1>
      </div>
      <p className="muted">Create, rename, and delete the tags used to organize recordings.</p>

      <form className="row" onSubmit={submit}>
        <input placeholder="New tag name" value={name} onChange={(e) => setName(e.target.value)} />
        <button disabled={!name.trim() || createTag.isPending}>
          {createTag.isPending ? 'Creating…' : 'Create tag'}
        </button>
      </form>
      {createTag.isError && <p className="error">{(createTag.error as Error).message}</p>}

      {error && (
        <div className="banner banner-danger">
          <p>{(error as Error).message || 'Failed to load tags.'}</p>
          <button className="sm" onClick={() => void refetch()}>
            Retry
          </button>
        </div>
      )}
      {isLoading && <p className="muted">Loading tags…</p>}
      {tags && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Created</th>
                <th className="right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td className="empty" colSpan={3}>
                    No tags yet — create one above to start tagging recordings.
                  </td>
                </tr>
              )}
              {items.map((t) => (
                <tr key={t.id}>
                  <td>
                    <span className="folder-row-main">
                      <span
                        className="folder-dot"
                        style={{ background: t.color ?? folderDotColor(t.id) }}
                        aria-hidden="true"
                      />
                      {renamingId === t.id ? (
                        <input
                          aria-label={`Rename ${t.name}`}
                          value={renameDraft}
                          autoFocus
                          onChange={(e) => setRenameDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveRename(t.id);
                            if (e.key === 'Escape') setRenamingId(null);
                          }}
                        />
                      ) : (
                        <span className="tag">{t.name}</span>
                      )}
                    </span>
                  </td>
                  <td>{formatDate(t.created_at)}</td>
                  <td className="right">
                    <div className="actions">
                      {renamingId === t.id ? (
                        <>
                          <button
                            className="sm"
                            disabled={!renameDraft.trim() || updateTag.isPending}
                            onClick={() => saveRename(t.id)}
                          >
                            {updateTag.isPending ? 'Saving…' : 'Save'}
                          </button>
                          <button className="ghost sm" onClick={() => setRenamingId(null)}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="ghost sm" onClick={() => startRename(t)}>
                            Rename
                          </button>
                          {confirmDelete === t.id ? (
                            <>
                              <button
                                className="danger sm"
                                disabled={deleteTag.isPending}
                                onClick={() =>
                                  deleteTag.mutate(t.id, {
                                    onSettled: () => setConfirmDelete(null),
                                  })
                                }
                              >
                                Confirm
                              </button>
                              <button className="ghost sm" onClick={() => setConfirmDelete(null)}>
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button className="ghost sm" onClick={() => setConfirmDelete(t.id)}>
                              Delete
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {updateTag.isError && <p className="error">{(updateTag.error as Error).message}</p>}
      {deleteTag.isError && <p className="error">{(deleteTag.error as Error).message}</p>}
    </div>
  );
}
